import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Eye, FileSignature, Loader2, Mail, Send } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/batico/proposition`;
const frDT = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const STATUTS = [
  { v: '', label: 'Tous' },
  { v: 'non_envoye', label: 'Non envoyés', cls: 'bg-slate-100 text-slate-600' },
  { v: 'envoye', label: 'Envoyés', cls: 'bg-sky-100 text-sky-700', Icon: Mail },
  { v: 'ouvert', label: 'Ouverts', cls: 'bg-amber-100 text-amber-800', Icon: Eye },
  { v: 'signe', label: 'Signés', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
];
const LIB = { non_envoye: 'Non envoyé', envoye: 'Envoyé', ouvert: 'Ouvert', signe: 'Signé' };

/** Suivi des signatures des Propositions ESS (COOPER'S : ses membres ; administration : tout). */
export default function SuiviSignatures() {
  const [statut, setStatut] = useState('envoye');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [envoi, setEnvoi] = useState(null);

  const charger = useCallback(() => {
    axios.get(`${API}/admin/suivi-signatures`, { params: { statut, q }, headers: headers() })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], compteurs: {}, total: 0 }));
  }, [statut, q]);
  useEffect(() => { setData(null); const t = setTimeout(charger, 300); return () => clearTimeout(t); }, [charger]);

  const relancer = async (x) => {
    if (!window.confirm(`Renvoyer le lien de signature du devis ${x.devis_id} par e-mail ?`)) return;
    setEnvoi(x.devis_id);
    try {
      const { data: r } = await axios.post(`${API}/${x.devis_id}/relance-signature`, {}, { headers: headers() });
      toast.success(`Relance envoyée à ${r.to_email}`);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    } finally { setEnvoi(null); }
  };

  return (
    <div className="space-y-4" data-testid="suivi-signatures">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-bold text-[#0a3d62]"><FileSignature className="h-5 w-5" /> Suivi des signatures</h2>
        <p className="text-xs text-slate-500">Propositions ESS envoyées, ouvertes (page de signature consultée) et signées</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {STATUTS.map((s) => (
          <button key={s.v || 'tous'} type="button" onClick={() => setStatut(s.v)} data-testid={`suivi-filtre-${s.v || 'tous'}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${statut === s.v ? 'bg-[#0a3d62] text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
            {s.label}{s.v && data?.compteurs ? ` (${data.compteurs[s.v] ?? 0})` : ''}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Membre, n° de devis ou e-mail"
          className="ml-auto h-9 w-72 rounded-lg border border-slate-200 px-3 text-sm" data-testid="suivi-recherche" />
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <SuiviTable items={data.items} envoi={envoi} onRelance={relancer} />}
    </div>
  );
}

function SuiviTable({ items, envoi, onRelance }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full min-w-[1000px] text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="p-2">Devis</th><th className="p-2">Membre</th><th className="p-2 text-right">Montant</th><th className="p-2">Statut</th><th className="p-2">Envoi</th><th className="p-2">Ouverture</th><th className="p-2">Signature</th><th className="p-2" /></tr>
        </thead>
        <tbody>
          {items.map((x) => {
            const s = STATUTS.find((k) => k.v === x.statut);
            return (
              <tr key={x.devis_id} className="border-t border-slate-100 align-top" data-testid={`suivi-ligne-${x.devis_id}`}>
                <td className="p-2 font-mono text-xs">{x.devis_id}<span className="block text-[10px] text-slate-400">créé le {frDT(x.created_at)}</span></td>
                <td className="p-2 font-semibold text-[#0a3d62]">{x.membre}<span className="block text-[10px] font-normal text-slate-400">{x.membre_email || ''}</span></td>
                <td className="whitespace-nowrap p-2 text-right tabular-nums">{eur(x.montant_ttc)}</td>
                <td className="p-2"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s?.cls}`} data-testid={`suivi-statut-${x.devis_id}`}>{s?.Icon && <s.Icon className="h-3 w-3" />}{LIB[x.statut]}</span></td>
                <td className="p-2 text-xs text-slate-600">{x.nb_envois ? <>{x.nb_envois} envoi(s)<span className="block">dernier : {frDT(x.dernier_envoi)}</span><span className="block text-slate-400">{x.envoye_a}</span></> : '—'}</td>
                <td className="p-2 text-xs text-slate-600">{x.nb_vues ? <>{x.nb_vues} visite(s)<span className="block">1re : {frDT(x.premiere_vue)}</span><span className="block">dernière : {frDT(x.derniere_vue)}</span></> : '—'}</td>
                <td className="p-2 text-xs">{x.signe_le ? <span className="font-semibold text-emerald-700">{frDT(x.signe_le)}<span className="block font-normal text-slate-500">{x.signataire}</span>
                  {x.signe_pour_membre && (
                    <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`suivi-signe-pour-${x.devis_id}`}
                      title={x.signe_pour_membre.par_email}>
                      Signé par {x.signe_pour_membre.par_role === 'cooper' ? "COOPER'S" : 'Administration'} : {x.signe_pour_membre.par_nom}
                    </span>
                  )}</span> : '—'}</td>
                <td className="p-2">
                  {x.statut !== 'signe' && (
                    <button type="button" onClick={() => onRelance(x)} disabled={envoi === x.devis_id} data-testid={`suivi-relancer-${x.devis_id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#0a3d62] px-3 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
                      {envoi === x.devis_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{x.nb_envois ? 'Relancer' : 'Envoyer'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {!items.length && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Aucune proposition</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
